import React, { useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { useQuery } from '@apollo/client/react';
import { c, msgid } from 'ttag';

import { Badge, Button, LoaderPage } from '@freelancelabs/components';
import { ArrowLeft } from '@freelancelabs/icons';

import Card from '../../components/Card';
import { MatrixSkeleton } from '../../components/Skeletons';
import { DataScope } from '../../graphql/__generated__/schema';
import {
    PermissionsDocument,
    PortalModulesDocument,
    PortalUserDocument,
    PortalUserQuery,
    RolesDocument,
} from '../../graphql/__generated__/types';
import RightsMatrix from '../roles/RightsMatrix';
import ChangeRoleDialog from './ChangeRoleDialog';
import DashboardsCard from './DashboardsCard';
import ReinstateMembershipDialog from './ReinstateMembershipDialog';
import ResendInvitationDialog, { resendButtonLabel } from './ResendInvitationDialog';
import { EMPTY, dataScopeLabel, formatDate, formatDateTime, mfaLabel } from './display';
import { invitationChaseSentence, invitationSentence, invitationStateOf, resendBlocker } from './invitation';
import {
    closureAuthorSentence,
    closureIsHistorySentence,
    closureReasonSentence,
    closureStateOf,
    reinstateOfferOf,
} from './revocation';
import { statusCategory, statusLabel } from './status';

type Membership = NonNullable<NonNullable<PortalUserQuery['portalUser']>['memberships']>[number];

/**
 * The sheet of one portal user: behind « Plus de détails » of the list (mockup §5.2).
 *
 * **It describes the person, not one membership.** A person belongs to a single
 * client and may hold several memberships inside its tree, each with its own role,
 * status and perimeter; the mockup's onboarding creates them in one pass. The
 * memberships shown are bounded by the accounts the actor may see, which is what
 * keeps the aggregation from widening anything.
 *
 * **Two gestures live here, and they are one loop.** Resending the invitation
 * (PCG-93) and reopening a closed access (PCG-94) both sit on the sheet rather than
 * on the list because they repair states only the sheet makes legible: an invitation
 * expired or chased three times, an access closed for a reason somebody wrote, and a
 * button whose reason is off screen is a button nobody knows when to press.
 *
 * **Attention, they chain, and neither is complete alone.** Reopening sends no email and
 * creates no invitation: a person who never activated comes back « invitée » with
 * nothing to activate, which is why the reopening dialog ends on the resend. The two
 * refusals that used to say « rouvrez d'abord un rattachement » now name a gesture
 * this screen really performs.
 *
 * **A third gesture landed with PCG-106, and it hangs from something else.** Granting
 * or withdrawing a dashboard is not done to the person but to **one of their
 * memberships**, which is why it lives in its own card grouped by account rather than
 * in the header beside the two above. `DashboardsCard` holds that pair and its two
 * dialogs.
 *
 * A fourth gesture landed with PCG-332: changing the role of one membership, from a
 * button on its row and the dialog `ChangeRoleDialog`. It acts on one precise
 * membership, so it is never ambiguous, and it does not touch the per-account model.
 *
 * The rest is still read-only: `updateMembershipPerimeter` is wired to nothing, being
 * an editing screen of its own, and `revokeMembership` (the other half of the reopen
 * pair) has no screen either, so an access can be reopened here but not closed. The
 * sections the API does not feed are **named** rather than hidden: a missing card
 * reads as an oversight.
 */
const UserDetailPage = () => {
    const { userId } = useParams<{ userId: string }>();
    const [resending, setResending] = useState(false);
    // The membership whose reopening is being confirmed, by id: holding the object
    // would keep rendering the row as it was before the refetch.
    const [reinstating, setReinstating] = useState<string | null>(null);
    // The membership whose role is being changed, by id (PCG-332), same reason as above.
    const [changingRole, setChangingRole] = useState<string | null>(null);
    const { data, loading, error } = useQuery(PortalUserDocument, {
        variables: { userId: userId ?? '' },
        skip: !userId,
    });
    const { data: rolesData } = useQuery(RolesDocument);
    // The rights matrix reads two referentials: the rights, and the modules that name and
    // order its groups since PCG-99. Both, or the matrix files every module right under
    // « module inconnu » for as long as one of them is in flight.
    const {
        data: referentialData,
        loading: referentialLoading,
        error: referentialError,
    } = useQuery(PermissionsDocument);
    const { data: modulesData, loading: modulesLoading, error: modulesError } = useQuery(PortalModulesDocument);

    const backToUsers = (
        <Link to="/utilisateurs" className="link inline-flex items-center gap-1.5 text-sm">
            <ArrowLeft size={14} />
            {c('Users').t`Users`}
        </Link>
    );

    /**
     * **Le chargement INITIAL seulement, jamais un rechargement de fond** (PCG-116).
     *
     * `loading` repasse à vrai à chaque `refetchQueries: [PortalUserDocument]`, et
     * les quatre gestes de cette fiche en demandent un : renvoyer l'invitation,
     * attribuer un tableau de bord, en retirer un, rouvrir un accès. Rendre le
     * loader à ce moment-là **démonte la modale ouverte**, donc l'état de son
     * `useMutation` avec elle. Elle se remonte ensuite sur son écran de départ,
     * sans un mot : l'opérateur croit que rien ne s'est passé.
     *
     * Ce n'est pas qu'un désagrément. Un renvoi d'invitation **fait tourner le
     * jeton** : recliquer envoie un second courriel et tue le lien du premier, si
     * bien que la personne reçoit deux messages dont un seul fonctionne. Constaté
     * en recette, et c'est exactement ce que le geste sans retour provoque.
     *
     * Une fiche qui a déjà ses données ne se vide donc pas pour aller les
     * rafraîchir. Le loader reste pour ce qu'il sert : la première ouverture,
     * quand il n'y a rien à montrer.
     */
    if (loading && !data) {
        return <LoaderPage />;
    }

    const user = data?.portalUser;

    /**
     * A failed query is **not** an absent user, and saying « introuvable » when the
     * query broke sends the reader looking for a person who is right there. It cost
     * an afternoon once: the API had renamed a field, the query no longer validated,
     * and the screen answered « cet utilisateur n'existe pas ». The message now
     * carries what the server said.
     */
    if (error) {
        return (
            <section className="flex flex-col gap-3">
                {backToUsers}
                <h1 className="text-xl font-semibold text-text-900">{c('Users').t`This sheet could not be loaded`}</h1>
                <p className="text-text-600">{c('Users').t`The server refused the request.`}</p>
                <p className="max-w-3xl rounded-md border border-border-norm bg-background-weak p-3 font-mono text-xs text-text-600">
                    {error.message}
                </p>
            </section>
        );
    }

    // Out of perimeter and non-existent give the same answer, by design of the API:
    // the screen must not say more than the query does.
    if (!user) {
        return (
            <section className="flex flex-col gap-3">
                {backToUsers}
                <h1 className="text-xl font-semibold text-text-900">{c('Users').t`User not found`}</h1>
                <p className="text-text-600">
                    {c('Users').t`This user does not exist, or holds no membership on an account you can see.`}
                </p>
            </section>
        );
    }

    const memberships = (user.memberships ?? []).filter(Boolean) as Membership[];
    const fullName = [user.firstName, user.lastName].filter(Boolean).join(' ') || (user.email ?? EMPTY);
    // PCG-341. Zero (an assigned but empty list) is distinct from a null partnerList (no list).
    const partnerCount = user.partnerList?.partnerCount ?? 0;
    /** PCG-305 : les DEUX listes, additionnées. C'est ce qu'un périmètre restreint retient. */
    const restrictedCount = (membership: Membership) =>
        (membership.visibleNeedCount ?? 0) + (membership.visibleServiceCount ?? 0);

    const roles = [...(rolesData?.portal ?? []), ...(rolesData?.backoffice ?? [])].filter(Boolean);
    // Only portal roles are offered on a client membership: a back-office role refuses.
    const portalRoles = (rolesData?.portal ?? []).filter(Boolean);
    const referential = (referentialData?.permissions ?? []).filter(Boolean);
    const modules = modulesData?.portalModules ?? [];
    const matrixLoading = referentialLoading || modulesLoading;
    const matrixError = referentialError ?? modulesError;
    // The distinct roles this person holds. Several is not an anomaly: administrator
    // of the parent company and plain reader on a subsidiary is a common case, and
    // that is why the model carries the role on the membership.
    const heldRoleIds = [...new Set(memberships.map((membership) => membership.roleId))];
    // The anchor belongs to the person, not to one of their memberships: a list, because
    // a person may be known by several source systems, and empty when the account was
    // created by hand. The emitter is named next to its identifier: « SF-00412 » alone
    // says nothing about where to go and look.
    const externalReferences = (user.externalReferences ?? [])
        .filter(Boolean)
        .map((reference) => `${reference.externalId} (${reference.sourceSystem})`)
        .join(' · ');

    // Where the invitation stands, and whether resending it can work. Both are read
    // from the same row, so the card and its button cannot contradict each other.
    const invitationState = invitationStateOf(user);
    const chaseSentence = invitationChaseSentence(invitationState);
    const blocker = resendBlocker(user);
    // A **pending** invitation is the only thing that fills `invitationExpiresAt`, so
    // this is « il existe un lien vivant », which is what the resend dialog needs to
    // know before warning anybody about losing one.
    const hasLiveLink = Boolean(user.invitationExpiresAt);

    /**
     * Attention, the closures, each read **against its membership's status**. The three
     * `revoked*` fields describe the last closure and are not cleared by a reopening,
     * so they sit on memberships that are alive today: rendering them raw would
     * announce a closure that has been undone. `closureStateOf` is the single place
     * that reading happens.
     */
    const closures = memberships
        .map((membership) => ({ membership, closure: closureStateOf(membership) }))
        .filter((entry) => entry.closure.kind !== 'none')
        // What is closed now comes first: it is the only part carrying a gesture.
        .sort((first, second) => Number(second.closure.kind === 'current') - Number(first.closure.kind === 'current'));

    const reinstatingEntry = closures.find((entry) => entry.membership.membershipId === reinstating);
    const changingRoleMembership = memberships.find((membership) => membership.membershipId === changingRole);

    return (
        <section className="flex flex-col gap-5">
            {backToUsers}

            <header className="flex flex-wrap items-start gap-4">
                <div>
                    <h1 className="text-xl font-semibold text-text-900">{fullName}</h1>
                    <p className="mt-1 text-xs text-text-600">
                        {user.function ? `${user.function} · ` : ''}
                        <span className="font-mono text-xs">{user.email}</span>
                    </p>
                </div>
                {user.disabledAt && (
                    <Badge category="default" size="small">
                        {c('Users').t`Account disabled`}
                    </Badge>
                )}
            </header>

            <Card title={c('Users').t`Identity and status`}>
                <dl className="grid grid-cols-1 gap-x-10 gap-y-4 sm:grid-cols-2 lg:grid-cols-3">
                    <Field label={c('Users column').t`Last name`} value={user.lastName ?? EMPTY} />
                    <Field label={c('Users column').t`First name`} value={user.firstName ?? EMPTY} />
                    <Field label={c('Users column').t`Email`} value={user.email ?? EMPTY} mono />
                    <Field label={c('Users').t`Job title`} value={user.function ?? EMPTY} />
                    {/* « Activité » et non « connexion » (PCG-120) : l'API ne voit pas
                        l'authentification, elle voit une requête portant un jeton
                        encore valide, qu'un rafraîchissement renouvelle des jours
                        durant. Les deux dates peuvent donc s'éloigner, et c'est le
                        passage qui est mesuré.

                        Attention, la valeur est arrondie à l'heure : elle n'est
                        réécrite qu'au-delà d'un seuil, pour ne pas transformer chaque
                        requête du portail en écriture. « Il y a trois heures » est
                        fiable, « il y a deux minutes » ne le serait pas. */}
                    <Field label={c('Users').t`Last activity`} value={formatDateTime(user.lastLoginAt)} mono />
                    <Field label={c('Users column').t`Invited on`} value={formatDate(user.invitedAt)} mono />
                    <Field
                        label={c('Users column').t`Account created`}
                        value={user.activatedAt ? formatDate(user.activatedAt) : c('Users').t`Awaiting activation`}
                        mono={Boolean(user.activatedAt)}
                    />
                    {/* PCG-303 : l'ETAT vient du POOL, lu a la demande, et l'EVENEMENT
                        vient de chez nous. Trois etats et non deux : des facteurs, aucun
                        facteur, et « non lu » quand le pool n'a pas repondu, qui ne se
                        confond avec aucun des deux. Et « aucun facteur » ne dit pas « non
                        conforme » : l'exigence peut n'avoir jamais ete posee sur ce compte. */}
                    <Field
                        label={c('Users').t`Two-factor authentication`}
                        value={mfaLabel(user.mfa, user.mfaEnabledAt)}
                        mono={Boolean(user.mfaEnabledAt)}
                    />
                    {/* The expiry and the send count used to sit here as two bare
                        dates. They moved to the « Invitation » card below, where they
                        are read rather than printed: « valide jusqu'au 19/07 » leaves
                        the operator to work out that it died four days ago. */}
                    {user.disabledReason && (
                        <Field label={c('Users').t`Reason for deactivation`} value={user.disabledReason} />
                    )}
                    <Field
                        label={c('Users').t`External references`}
                        value={externalReferences || EMPTY}
                        mono={Boolean(externalReferences)}
                    />
                </dl>
            </Card>

            {/* Rendered whenever the person is not in yet, including when the
                button is refused. The card is where the state becomes legible, and a
                card that appeared only when the gesture is possible would hide the
                very situation the operator has to understand. */}
            {invitationState.kind !== 'none' && (
                <Card title={c('Users').t`Invitation`}>
                    <p className="text-sm text-text-900">{invitationSentence(invitationState)}</p>
                    {chaseSentence && <p className="mt-2 text-xs text-text-600">{chaseSentence}</p>}
                    {/* « Invitée le » stays on the identity card, where it is visible
                        for an activated person too. What belongs here is what the
                        sentence above is made of. */}
                    <dl className="mt-4 grid grid-cols-1 gap-x-10 gap-y-4 sm:grid-cols-2 lg:grid-cols-3">
                        {/* Not rendered when no link exists: « valide jusqu'au, »
                            reads as a date that failed to load, where the sentence
                            above has just said there is no link at all. */}
                        {invitationState.kind !== 'missing' && (
                            <Field
                                label={c('Users').t`Invitation valid until`}
                                value={formatDate(user.invitationExpiresAt)}
                                mono
                            />
                        )}
                        <Field label={c('Users').t`Invitations sent`} value={String(invitationState.sentCount)} mono />
                    </dl>
                    <div className="mt-4 flex flex-wrap items-center gap-3">
                        {/* Disabled with its reason, never removed: a button that
                            vanishes leaves nobody able to ask why. */}
                        <Button
                            category="default"
                            disabled={Boolean(blocker)}
                            title={blocker ?? undefined}
                            onClick={() => setResending(true)}
                        >
                            {resendButtonLabel(invitationState.sentCount)}
                        </Button>
                        {blocker && <p className="text-xs text-text-600">{blocker}</p>}
                    </div>
                </Card>
            )}

            {resending && user.userId && (
                <ResendInvitationDialog
                    userId={user.userId}
                    email={user.email ?? ''}
                    hasLiveLink={hasLiveLink}
                    onClose={() => setResending(false)}
                />
            )}

            {reinstatingEntry?.membership.membershipId && (
                <ReinstateMembershipDialog
                    membershipId={reinstatingEntry.membership.membershipId}
                    organizationName={reinstatingEntry.membership.organizationName ?? EMPTY}
                    roleName={reinstatingEntry.membership.roleName ?? EMPTY}
                    closure={reinstatingEntry.closure}
                    onClose={() => setReinstating(null)}
                    // The second half of the gesture, handed over rather than
                    // described: the reopened person is « invitée » with nothing to
                    // activate, and the invitation card cannot offer it: a cancelled
                    // invitation leaves no expiry for the card to key on until the
                    // refetch lands.
                    onResendInvitation={() => {
                        setReinstating(null);
                        setResending(true);
                    }}
                />
            )}

            {changingRoleMembership?.membershipId && (
                <ChangeRoleDialog
                    membershipId={changingRoleMembership.membershipId}
                    organizationName={changingRoleMembership.organizationName ?? EMPTY}
                    personName={fullName}
                    currentRoleId={changingRoleMembership.roleId ?? null}
                    currentRoleName={changingRoleMembership.roleName ?? EMPTY}
                    roles={portalRoles}
                    onClose={() => setChangingRole(null)}
                />
            )}

            <Card
                title={c('Users').t`Accounts`}
                counter={memberships.length}
                hint={c('Users')
                    .t`One membership per account, each carrying its own role, status and perimeter. Only the accounts you can see are listed.`}
            >
                <div className="fl-table-container" tabIndex={0} role="region" aria-label={c('Users').t`Accounts`}>
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="border-b border-border-norm text-left text-xs uppercase tracking-wide text-text-500">
                                <th className="py-2 pr-4 font-semibold">{c('Users column').t`Account`}</th>
                                <th className="py-2 pr-4 font-semibold">{c('Users column').t`Role`}</th>
                                <th className="py-2 pr-4 font-semibold">{c('Users column').t`Status`}</th>
                                <th className="py-2 pr-4 font-semibold">{c('Users').t`Perimeter`}</th>
                                <th className="py-2 font-semibold">{c('Users').t`Joined on`}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {memberships.map((membership) => (
                                <tr key={membership.membershipId} className="border-b border-border-weak">
                                    <td className="py-2.5 pr-4">
                                        <span className="text-text-900">{membership.organizationName}</span>
                                        {membership.isDefault && (
                                            <span className="ml-2 text-xs text-text-500">
                                                {c('Users').t`(opened at sign-in)`}
                                            </span>
                                        )}
                                    </td>
                                    <td className="py-2.5 pr-4">
                                        <div className="flex flex-col items-start gap-1">
                                            <span className="text-text-900">{membership.roleName}</span>
                                            {membership.membershipId && (
                                                <Button
                                                    category="weak"
                                                    size="small"
                                                    onClick={() => setChangingRole(membership.membershipId ?? null)}
                                                >
                                                    {c('Users').t`Change the role`}
                                                </Button>
                                            )}
                                        </div>
                                    </td>
                                    <td className="py-2.5 pr-4">
                                        {membership.status && (
                                            <Badge category={statusCategory(membership.status)} size="small">
                                                {statusLabel(membership.status)}
                                            </Badge>
                                        )}
                                    </td>
                                    <td className="py-2.5 pr-4">
                                        {dataScopeLabel(membership.dataScope, restrictedCount(membership))}
                                    </td>
                                    <td className="py-2.5 font-mono text-xs">
                                        {formatDate(membership.joinedAt ?? membership.createdAt)}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                {/* What of a membership can be changed here, and what only read. */}
                <p className="mt-3 text-xs text-text-600">
                    {c('Users').ngettext(
                        msgid`Editing is limited on the ${memberships.length} membership of this person: its perimeter and its closing are read here, not changed. Three gestures are the exceptions: changing the role, reopening a closed access, and granting or withdrawing a dashboard.`,
                        `Editing is limited on the ${memberships.length} memberships of this person: their perimeter and their closing are read here, not changed. Three gestures are the exceptions: changing the role, reopening a closed access, and granting or withdrawing a dashboard.`,
                        memberships.length
                    )}
                </p>
            </Card>

            {/* Rendered as soon as **any** closure is on record, including ones
                that have been undone: a closure survives its reopening and stays
                readable, which is what makes the reason available the next time.
                Each entry says which of the two it is; the card never says on its
                own that an access is closed. */}
            {closures.length > 0 && (
                <Card
                    title={c('Users').t`Closures`}
                    hint={c('Users')
                        .t`The reason for a closure is read before reopening, not after. A closure stays on record once it has been undone, so an entry here is not by itself a closed access: the status says which.`}
                >
                    <div className="flex flex-col gap-5">
                        {closures.map(({ membership, closure }) => {
                            const offer = reinstateOfferOf(membership, closure);
                            const historySentence = closureIsHistorySentence(closure, membership);

                            return (
                                <div key={membership.membershipId} className="flex flex-col gap-1.5">
                                    <div className="flex flex-wrap items-center gap-2">
                                        <h3 className="text-sm font-semibold text-text-900">
                                            {membership.organizationName}
                                        </h3>
                                        {membership.status && (
                                            <Badge category={statusCategory(membership.status)} size="small">
                                                {statusLabel(membership.status)}
                                            </Badge>
                                        )}
                                    </div>
                                    <p className="text-sm text-text-900">{closureAuthorSentence(closure)}</p>
                                    <p className="text-xs text-text-600">{closureReasonSentence(closure)}</p>
                                    {historySentence && <p className="text-sm text-text-500">{historySentence}</p>}
                                    {offer.offered && (
                                        <div className="mt-1 flex flex-wrap items-center gap-3">
                                            {/* Disabled with its reason, never removed. */}
                                            <Button
                                                category="default"
                                                size="small"
                                                disabled={Boolean(offer.blocker)}
                                                title={offer.blocker ?? undefined}
                                                onClick={() => setReinstating(membership.membershipId ?? null)}
                                            >
                                                {c('Users').t`Reopen this access`}
                                            </Button>
                                            {offer.blocker && <p className="text-xs text-text-600">{offer.blocker}</p>}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </Card>
            )}

            <Card
                title={c('Users').t`Perimeter`}
                hint={c('Users')
                    .t`What the person may see, read from the same view as the portal — so the two cannot disagree.`}
            >
                <div className="flex flex-col gap-4">
                    {memberships.map((membership) => (
                        <div key={membership.membershipId}>
                            <h3 className="text-sm font-semibold text-text-900">{membership.organizationName}</h3>
                            {membership.dataScope === DataScope.Restricted ? (
                                (membership.visibleActivities ?? []).length > 0 ? (
                                    <>
                                        <ul className="mt-2 flex flex-col gap-1.5">
                                            {(membership.visibleActivities ?? []).filter(Boolean).map((activity) => (
                                                <li key={activity.id} className="flex flex-wrap items-baseline gap-2">
                                                    {/* Le TYPE est dit : un besoin et une prestation portent des
                                                        noms qui se ressemblent, et un administrateur qui vérifie
                                                        un périmètre doit savoir de quoi il lit la liste. */}
                                                    <span className="font-mono text-xs text-text-700">
                                                        {activity.kind === 'need'
                                                            ? c('Users').t`Need`
                                                            : c('Users').t`Service`}
                                                    </span>
                                                    <span className="text-sm text-text-900">
                                                        {activity.name ?? EMPTY}
                                                    </span>
                                                </li>
                                            ))}
                                        </ul>
                                        {/* CE QUE L'APERÇU NE MONTRE PAS, dit en toutes lettres. La liste est
                                            bornée à vingt par rattachement, et une liste tronquée en silence
                                            ferait croire que le périmètre s'arrête là. */}
                                        {restrictedCount(membership) > (membership.visibleActivities ?? []).length && (
                                            <p className="mt-1 text-xs text-text-600">
                                                {c('Users')
                                                    .t`Only the first activities are listed here. The count above is the whole perimeter.`}
                                            </p>
                                        )}
                                    </>
                                ) : (
                                    // The view reads `membership.status`: an invited person sees
                                    // nothing yet, whatever was configured for them.
                                    <p className="mt-1 text-xs text-text-600">
                                        {c('Users')
                                            .t`Restricted perimeter, nothing visible yet: the activities open when the membership becomes active.`}
                                    </p>
                                )
                            ) : (
                                <p className="mt-1 text-xs text-text-600">
                                    {c('Users')
                                        .t`No personal restriction: the person sees the whole account, including what is added later.`}
                                </p>
                            )}
                        </div>
                    ))}
                </div>
            </Card>

            {/* Right after the perimeter, which is the other answer to « que voit
                cette personne », and grouped the same way: by membership. */}
            <DashboardsCard memberships={memberships} personName={fullName} />

            <Card
                title={c('Users').t`Rights`}
                hint={c('Users')
                    .t`The rights come from the role. The whole referential is listed, granted or not: what a role forbids is as much part of it as what it allows.`}
            >
                {/* Said once for the whole card: an empty matrix under each role would read
                    as « ce rôle n'accorde rien », not as a referential that did not load. */}
                {matrixError ? (
                    <p className="text-sm text-danger-600" role="alert">
                        {c('Users').t`The rights referential could not be loaded.`}
                    </p>
                ) : matrixLoading ? (
                    <MatrixSkeleton />
                ) : (
                    <div className="flex flex-col gap-6">
                        {heldRoleIds.map((roleId) => {
                            const role = roles.find((candidate) => candidate.id === roleId);
                            const accounts = memberships
                                .filter((membership) => membership.roleId === roleId)
                                .map((membership) => membership.organizationName)
                                .join(', ');
                            const granted = new Set(
                                (role?.permissions ?? []).map((permission) => permission?.code ?? '')
                            );

                            return (
                                <div key={roleId}>
                                    <div className="mb-3 flex flex-wrap items-baseline gap-2">
                                        <h3 className="text-sm font-semibold text-text-900">
                                            {role?.name ??
                                                memberships.find((membership) => membership.roleId === roleId)
                                                    ?.roleName}
                                        </h3>
                                        {/* Which accounts this role applies to. With one membership it
                                        is redundant; with several it is the whole point. */}
                                        {memberships.length > 1 && (
                                            <span className="text-xs text-text-600">{accounts}</span>
                                        )}
                                        {role && (
                                            <Link to={`/roles/${role.id}`} className="link text-xs">
                                                {c('Users').t`See the role`}
                                            </Link>
                                        )}
                                    </div>
                                    <RightsMatrix referential={referential} modules={modules} granted={granted} />
                                </div>
                            );
                        })}
                    </div>
                )}
            </Card>

            {/* PCG-341. The referring account manager of the account, resolved from the
                source (membership_account_manager scale), the same Interlocutor as the
                portal. Rendered per account; null is the ordinary case. */}
            <Card
                title={c('Users').t`Referring account manager`}
                hint={c('Users')
                    .t`The Freelance.com contact for the account, resolved from the source. The same for everyone on a given account.`}
            >
                <div className="flex flex-col gap-4">
                    {memberships.map((membership) => (
                        <div key={membership.membershipId}>
                            <h3 className="text-sm font-semibold text-text-900">{membership.organizationName}</h3>
                            {membership.accountManager ? (
                                <div className="mt-1 flex flex-col gap-0.5">
                                    <span className="text-sm text-text-900">
                                        {membership.accountManager.displayName}
                                        {membership.accountManager.title && (
                                            <span className="ml-2 text-xs text-text-500">
                                                {membership.accountManager.title}
                                            </span>
                                        )}
                                    </span>
                                    {membership.accountManager.email && (
                                        <a className="link text-xs" href={`mailto:${membership.accountManager.email}`}>
                                            {membership.accountManager.email}
                                        </a>
                                    )}
                                </div>
                            ) : (
                                <p className="mt-1 text-xs text-text-600">
                                    {c('Users').t`No referring account manager resolves on this account.`}
                                </p>
                            )}
                        </div>
                    ))}
                </div>
            </Card>

            {/* PCG-341. The partner list assigned to the person. A zero count (empty
                list) is distinct from a null list (none assigned). */}
            <Card title={c('Users').t`Partner list`}>
                {user.partnerList ? (
                    <div className="flex flex-wrap items-center gap-2">
                        <span className="text-sm text-text-900">{user.partnerList.name ?? EMPTY}</span>
                        {user.partnerList.isAnonymous && (
                            <Badge category="default" size="small">
                                {c('Users').t`Anonymous`}
                            </Badge>
                        )}
                        <span className="text-xs text-text-600">
                            {c('Users').ngettext(
                                msgid`${partnerCount} partner`,
                                `${partnerCount} partners`,
                                partnerCount
                            )}
                        </span>
                    </div>
                ) : (
                    <p className="text-xs text-text-600">{c('Users').t`No partner list is assigned to this person.`}</p>
                )}
            </Card>
        </section>
    );
};

interface FieldProps {
    label: string;
    value: string;
    mono?: boolean;
}

const Field = ({ label, value, mono }: FieldProps) => (
    <div className="min-w-0">
        <dt className="text-xs uppercase tracking-wide text-text-500">{label}</dt>
        <dd className={`mt-0.5 break-words text-text-900 ${mono ? 'font-mono text-xs' : 'text-sm'}`}>{value}</dd>
    </div>
);

export default UserDetailPage;
